import type { SalesPoint } from "../services/salesPointAPI";
import type {
  BillReceiptItem,
  BillReceiptSale,
} from "../components/sales/BillReceiptPreview";

export function resolveSaleBillDate(sale: {
  completedAt?: string | null;
  createdAt?: string | null;
}): string {
  const raw = sale.completedAt || sale.createdAt;
  return raw || new Date().toISOString();
}

export function normalizeBillItems(raw: unknown): BillReceiptItem[] {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item: any) => {
    const product = item?.product;
    const qty = Number(item?.quantity) || 0;
    const unit = Number(item?.unitPrice) || 0;
    const net = Number(item?.netAmount ?? item?.totalPrice) || unit * qty;
    return {
      productName:
        item?.productName ||
        item?.product_name ||
        product?.name ||
        "Item",
      name: product?.name,
      quantity: qty,
      unitPrice: unit,
      netAmount: net,
      totalPrice: net,
      gstPercentage:
        Number(item?.gstPercentage ?? product?.gstPercentage) || 0,
      isTaxInclusive: Boolean(
        item?.isTaxInclusive ?? product?.isTaxInclusive ?? true
      ),
      notes: item?.notes || undefined,
    };
  });
}

export function toBillReceiptSale(
  sale: Record<string, any>,
  salesPoint?: SalesPoint | null
): BillReceiptSale {
  const saleNumber = String(sale.saleNumber || sale.orderNumber || "");
  const isRandom =
    saleNumber.toUpperCase().startsWith("RND") ||
    String(sale.notes || "")
      .toLowerCase()
      .includes("random sale");

  return {
    saleNumber,
    tableNumber: sale.tableNumber || sale.table_number,
    orderType: sale.orderType || sale.order_type,
    totalAmount: Number(sale.totalAmount) || 0,
    discountAmount: Number(sale.discountAmount) || 0,
    taxAmount: Number(sale.taxAmount) || 0,
    cgstAmount: Number(sale.cgstAmount) || 0,
    sgstAmount: Number(sale.sgstAmount) || 0,
    igstAmount: Number(sale.igstAmount) || 0,
    netAmount: Number(sale.netAmount) || 0,
    paymentMethod: sale.paymentMethod || "cash",
    customerName: sale.customerName || sale.customer_name,
    cardHolder: sale.cardHolder,
    saleItems: normalizeBillItems(sale.saleItems || sale.items),
    createdAt: sale.createdAt,
    completedAt: sale.completedAt,
    saleType: isRandom ? "random" : undefined,
    storeName: salesPoint?.name || sale.salesPoint?.name,
    storeAddress: salesPoint?.address ?? sale.salesPoint?.address,
    storePhone: salesPoint?.phoneNo ?? sale.salesPoint?.phoneNo,
    storeGstin: salesPoint?.gstin ?? sale.salesPoint?.gstin,
    storeLicence: salesPoint?.licence ?? sale.salesPoint?.licence,
    storeMessage: salesPoint?.message ?? sale.salesPoint?.message,
    taxMode: salesPoint?.taxMode ?? sale.salesPoint?.taxMode,
    cashierName: sale.createdByUser?.name || sale.cashierName,
  };
}

export function billSeqDigits(saleNumber?: string | null): string {
  const raw = String(saleNumber || "").trim();
  const m = raw.match(/(\d+)\s*$/);
  return m ? m[1].padStart(5, "0") : raw || "—";
}
